import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { backendUrl } from "@/lib/api";
import { Loader2, HeartHandshake, Building2, Wheat, Palmtree, Download, Send } from "lucide-react";
import InviaFlussiDialog from "@/components/InviaFlussiDialog";

const ICONE = {
  assistenza_familiare: HeartHandshake,
  non_stagionale: Building2,
  stagionale_agricolo: Wheat,
  stagionale_turistico: Palmtree,
};
const fileUrl = (id, scarica) =>
  `${backendUrl}/api/primo-piano/file/${id}?token=${localStorage.getItem("rb_token")}${scarica ? "&scarica=1" : ""}`;

function Scheda({ cat, onInvia }) {
  const Icona = ICONE[cat.codice] || Building2;
  return (
    <div className="sub flex flex-col bg-white transition-shadow hover:shadow-[0_10px_24px_-18px_rgba(27,42,75,0.55)]"
      data-testid={`flussi-card-${cat.codice}`}>
      <div className="subt items-start">
        <span className="flex items-center gap-2 min-w-0">
          <Icona className="w-[18px] h-[18px] shrink-0 text-[#A8863F]" />
          <span className="truncate">{cat.nome}</span>
        </span>
      </div>
      <p className="px-3.5 pt-3 pb-1 text-[13px] text-[#5A5A5A] leading-snug">{cat.nota}</p>
      <div className="flex flex-col">
        {cat.documenti.map((d) => (
          <div key={d.file_id} className="riga group gap-2" data-testid={`flussi-doc-${cat.codice}-${d.destinatario}`}>
            <a href={fileUrl(d.file_id)} target="_blank" rel="noreferrer"
              className="flex-grow min-w-0 no-underline text-[#1A1A1A] hover:text-[#1B2A4B]"
              data-testid={`flussi-apri-${cat.codice}-${d.destinatario}`}>
              <span className="block truncate font-semibold text-[14px]">{d.titolo}</span>
              <span className="block text-[12px] text-[#5A5A5A] truncate">{d.nota}</span>
            </a>
            <a href={fileUrl(d.file_id, true)} title="Scarica il PDF"
              data-testid={`flussi-scarica-${cat.codice}-${d.destinatario}`}
              className="shrink-0 text-[#5A5A5A] hover:text-[#1B2A4B] opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity">
              <Download className="w-4 h-4" />
            </a>
          </div>
        ))}
      </div>
      <div className="mt-auto px-3.5 py-3 border-t border-[#F0EEE8]">
        <button onClick={() => onInvia(cat)} data-testid={`flussi-invia-${cat.codice}`}
          className="btn btn-o btn-piccolo w-full">
          <Send className="w-4 h-4" /> Invia a un cliente
        </button>
      </div>
    </div>
  );
}

// «In primo piano»: i 12 PDF del Decreto Flussi divisi per categoria, da aprire o spedire in due clic.
export default function PrimoPiano() {
  const [invia, setInvia] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["primo-piano-flussi"],
    queryFn: async () => (await api.get("/primo-piano/decreto-flussi")).data,
    staleTime: 1000 * 60 * 10,
  });
  const categorie = data?.categorie || [];

  return (
    <div className="flex flex-col gap-3.5" data-testid="primo-piano-flussi">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-[19px] md:text-[21px] font-bold text-[#1B2A4B]" data-testid="primo-piano-titolo">
          Speciale Decreto Flussi 2027
        </h3>
        <span className="text-[13px] text-[#5A5A5A]">
          quattro categorie · dodici schede pronte da mandare
        </span>
      </div>

      {isLoading ? (
        <div className="sub"><div className="riga text-[#5A5A5A]">
          <Loader2 className="w-4 h-4 animate-spin" /> Leggo le schede da Drive…
        </div></div>
      ) : categorie.length === 0 ? (
        <div className="sub"><div className="riga text-[#5A5A5A]" data-testid="primo-piano-vuoto">
          {data?.errore || "Nessuna scheda disponibile."}
        </div></div>
      ) : (
        <>
          <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4 min-w-0">
            {categorie.map((c) => <Scheda key={c.codice} cat={c} onInvia={setInvia} />)}
          </div>
          {data?.errore && (
            <p className="text-[13px] text-[#9A3B13]" data-testid="primo-piano-avviso">{data.errore}</p>
          )}
        </>
      )}

      {invia && <InviaFlussiDialog categoria={invia} onClose={() => setInvia(null)} />}
    </div>
  );
}
